import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tag } from "lucide-react";
import { useMonitoringTags } from "@/hooks/useMonitoringTags";
import { TagBadge } from "./TagBadge";

interface TagSelectorProps {
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
}

export function TagSelector({ selectedTagIds, onTagsChange }: TagSelectorProps) {
  const { tags, loading } = useMonitoringTags();
  const [selected, setSelected] = useState<Set<string>>(new Set(selectedTagIds));

  useEffect(() => {
    setSelected(new Set(selectedTagIds));
  }, [selectedTagIds]);

  const toggleTag = (tagId: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelected(newSelected);
    onTagsChange(Array.from(newSelected));
  };

  if (loading) return null;

  if (tags.length === 0) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Tags de Monitoramento
          </CardTitle>
          <CardDescription className="text-xs">
            Nenhuma tag disponível. Crie tags em Minha Conta → Tags para categorizar documentos.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Tags de Monitoramento
        </CardTitle>
        <CardDescription className="text-xs">
          Selecione tags para categorizar este documento
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <button
              key={tag.id}
              onClick={() => toggleTag(tag.id)}
              className={`transition ${
                selected.has(tag.id) ? 'scale-105' : 'opacity-60 hover:opacity-100'
              }`}
            >
              <TagBadge 
                name={tag.name} 
                color={tag.color}
              />
            </button>
          ))}
        </div>
        {selected.size > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            {selected.size} tag{selected.size !== 1 ? 's' : ''} selecionada{selected.size !== 1 ? 's' : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
